import { FC } from 'react';

import { ChevronDown, History, PackageCheck } from 'lucide-react';

import { Badge } from '../../../../components/ui/Badge';
import { ActivePremiumService } from '../../../../types';
import { formatLocalDateTime } from '../../../../utils';

interface Props {
    activePremiumServices: ActivePremiumService[];
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

export const PurchasedPremiumServices: FC<Props> = ({
    activePremiumServices,
    open,
    onOpenChange,
}) => (
    <details
        id="premium-history"
        open={open}
        onToggle={(event) => onOpenChange(event.currentTarget.open)}
        className="group scroll-mt-24 rounded-2xl border bg-card"
        data-testid="premium-history"
    >
        <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-4 font-semibold [&::-webkit-details-marker]:hidden">
            <span className="flex items-center gap-2">
                <History className="h-5 w-5" />
                Історія придбаних наборів
            </span>
            <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
        </summary>

        <div className="border-t p-4 pt-3" data-testid="premium-owned-packages">
            {activePremiumServices.length > 0 ? (
                <div className="space-y-3">
                    {activePremiumServices.map((service) => (
                        <div
                            key={service.id}
                            className="grid gap-4 rounded-xl border bg-background p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center"
                            data-testid={`premium-owned-package-${service.id}`}
                        >
                            <div className="flex min-w-0 items-start gap-3">
                                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                                    <PackageCheck className="h-5 w-5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold">
                                            {service.service_name}
                                        </span>
                                        <Badge
                                            variant={
                                                service.status === 'active'
                                                    ? 'success'
                                                    : 'secondary'
                                            }
                                        >
                                            {service.status === 'active'
                                                ? 'Активний'
                                                : 'Вичерпаний'}
                                        </Badge>
                                    </div>
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        Придбано{' '}
                                        {formatLocalDateTime(
                                            service.started_at,
                                        )}
                                    </p>
                                </div>
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div
                                    className="rounded-xl bg-green-500/10 px-3 py-2"
                                    data-testid={`premium-package-available-${service.id}`}
                                >
                                    <div className="font-bold text-green-700 dark:text-green-400">
                                        {service.available_slots_count}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                        доступно
                                    </div>
                                </div>
                                <div className="rounded-xl bg-blue-500/10 px-3 py-2">
                                    <div className="font-bold text-blue-700 dark:text-blue-400">
                                        {service.active_slots_count}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                        активно
                                    </div>
                                </div>
                                <div className="rounded-xl bg-muted px-3 py-2">
                                    <div className="font-bold">
                                        {service.used_slots_count}
                                    </div>
                                    <div className="text-[11px] text-muted-foreground">
                                        використано
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="rounded-2xl border border-dashed px-5 py-7 text-center">
                    <PackageCheck className="mx-auto h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 text-sm font-medium">
                        Ви ще не купували premium-набори
                    </p>
                    <p className="mt-1 text-sm text-muted-foreground">
                        Після успішної оплати набір зʼявиться тут.
                    </p>
                </div>
            )}
        </div>
    </details>
);
